/* Book Lab · Phase 1.1 Quick-Action modals.
   Import file · Text-to-Image · KDP Preflight · Print Package ZIP.
   Every action hits real backend endpoints (see book_lab_routes.py). */
import { useState, useRef, useEffect, useCallback } from "react";
import {
  X, Loader2, Upload, Wand2, Image as ImageIcon, ShieldCheck, Package,
  RefreshCw, Trash2, Plus, CheckCircle2, AlertTriangle, XCircle, ArrowDownToLine,
} from "lucide-react";
import { api } from "../../../lib/apiClient";
import AuthedImage from "./AuthedImage";

/* ══════════════════════════════════════════════════════════════
 * IMPORT FILE MODAL
 * ══════════════════════════════════════════════════════════════ */
export function ImportFileModal({ bookId, onClose, onDone }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  const pick = () => inputRef.current?.click();
  const submit = async () => {
    if (!file) { setErr("Choose a .txt, .md, or .docx file first."); return; }
    setBusy(true); setErr(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("replace", replace ? "true" : "false");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/book-lab/books/${bookId}/import-file`,
        { method: "POST", headers: { Authorization: `Bearer ${api.token()}` }, body: fd },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || `Import failed (${res.status})`);
      setResult(body);
      await onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Import file" icon={Upload} onClose={onClose} testId="qa-modal-import">
      <div className="text-[12px] text-[var(--pl-text-muted)] mb-3">
        Upload a <code>.txt</code>, <code>.md</code>, or <code>.docx</code>. Chapter breaks are detected
        from <code># Heading</code> lines (Markdown) or <em>Heading&nbsp;1</em> styles (DOCX).
      </div>
      <input
        ref={inputRef} type="file" accept=".txt,.md,.docx" hidden
        onChange={(e) => { setFile(e.target.files?.[0] || null); setErr(null); setResult(null); }}
        data-testid="qa-import-input"
      />
      <div className="flex items-center gap-2 mb-3">
        <button onClick={pick} className="modal-btn" data-testid="qa-import-pick">
          <Plus className="h-3.5 w-3.5" /> Choose file…
        </button>
        <span className="text-[11.5px] text-[var(--pl-text-muted)]">
          {file ? `${file.name}  ·  ${(file.size / 1024).toFixed(1)} KB` : "No file selected"}
        </span>
      </div>
      <label className="flex items-center gap-2 text-[11.5px] text-[var(--pl-text)] mb-4">
        <input type="checkbox" checked={replace}
               onChange={(e) => setReplace(e.target.checked)}
               data-testid="qa-import-replace" />
        Replace existing chapters (default: append)
      </label>
      {err && <ErrLine>{err}</ErrLine>}
      {result && (
        <OkLine>
          Imported <strong>{result.imported}</strong> chapter(s).
          Book now has {result.chapters_total} chapter(s){result.replaced ? " (previous chapters replaced)" : ""}.
        </OkLine>
      )}
      <ModalFooter>
        <button onClick={onClose} className="modal-btn">Close</button>
        <button onClick={submit} disabled={busy || !file}
                className="modal-btn-primary" data-testid="qa-import-submit">
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {busy ? "Importing…" : "Import"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}


/* ══════════════════════════════════════════════════════════════
 * TEXT-TO-IMAGE MODAL  (OpenAI gpt-image-1)
 * ══════════════════════════════════════════════════════════════ */
const SIZES = [
  { key: "square",    label: "Square · 1024²",  wh: "1024×1024" },
  { key: "portrait",  label: "Portrait · 2:3",  wh: "1024×1536" },
  { key: "landscape", label: "Landscape · 3:2", wh: "1536×1024" },
];

export function TextToImageModal({ bookId, book, onClose, onDone }) {
  const [prompt, setPrompt] = useState("");
  const [size, setSize] = useState("square");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [preview, setPreview] = useState(null); // current generation
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [insertPage, setInsertPage] = useState("");

  const loadAssets = useCallback(async () => {
    try {
      const r = await api.get(`/api/book-lab/books/${bookId}/assets`);
      setAssets(r.items || []);
    } catch (e) { /* no-op */ }
  }, [bookId]);
  useEffect(() => { loadAssets(); }, [loadAssets]);

  const generate = async (replaceId) => {
    if (!prompt.trim()) { setErr("Enter a prompt (or refine the current one)."); return; }
    setBusy(true); setErr(null);
    try {
      const body = { prompt: prompt.trim(), size };
      if (replaceId) body.replace_asset_id = replaceId;
      const r = await api.post(`/api/book-lab/books/${bookId}/generate-image`, body);
      setPreview(r);
      setSelectedAssetId(r.id);
      await loadAssets();
      await onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const doDelete = async (assetId) => {
    if (!window.confirm("Delete this generated image? This cannot be undone.")) return;
    try {
      await api.del(`/api/book-lab/books/${bookId}/assets/${assetId}`);
      if (preview?.id === assetId) setPreview(null);
      if (selectedAssetId === assetId) setSelectedAssetId(null);
      await loadAssets();
      await onDone?.();
    } catch (e) { setErr(e.message); }
  };

  const insertIntoPage = async (assetId) => {
    const n = parseInt(insertPage, 10);
    if (!n || n < 1) { setErr("Enter a page number ≥ 1 first."); return; }
    setBusy(true); setErr(null);
    try {
      await api.patch(`/api/book-lab/books/${bookId}/assets/${assetId}`, { page_number: n });
      window.dispatchEvent(new CustomEvent("book-lab:insert-image", {
        detail: { bookId, assetId, page: n },
      }));
      await loadAssets();
      await onDone?.();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const chosen = assets.find((a) => a.id === selectedAssetId) || preview;

  return (
    <ModalShell title="Text to image · OpenAI gpt-image-1" icon={ImageIcon} onClose={onClose}
                testId="qa-modal-image" wide>
      <div className="text-[11.5px] text-[var(--pl-text-muted)] mb-2">
        Real backend call to <code>gpt-image-1</code>. Your key stays on the server. Every image is
        associated with this book, workspace, and account so cross-workspace access is impossible.
      </div>
      <div className="grid grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-1">
        {/* Left: form */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">Prompt</div>
          <textarea
            rows={5} value={prompt} data-testid="qa-image-prompt"
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A minimalist book-cover illustration of…"
            className="w-full rounded-md border border-[var(--pl-border)] bg-black/20 p-2 text-[12.5px] text-[var(--pl-text)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
          />
          <div className="mt-2 flex items-center gap-2">
            <button onClick={() => setPrompt((p) => p + "\n\nStyle: refined, editorial, restrained palette.")}
                    className="modal-btn text-[10px]" data-testid="qa-image-improve">
              <Wand2 className="h-3 w-3" /> Improve prompt
            </button>
          </div>

          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mt-3 mb-1">Orientation</div>
          <div className="grid grid-cols-3 gap-1.5">
            {SIZES.map((s) => (
              <button key={s.key}
                onClick={() => setSize(s.key)}
                data-testid={`qa-image-size-${s.key}`}
                className={"h-8 rounded-md text-[10.5px] border transition " +
                  (size === s.key
                    ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
                    : "border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40")}>
                {s.label}
              </button>
            ))}
          </div>

          {err && <ErrLine>{err}</ErrLine>}

          <div className="mt-3 flex items-center gap-2">
            <button onClick={() => generate(null)} disabled={busy || !prompt.trim()}
                    className="modal-btn-primary" data-testid="qa-image-generate">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}
              {busy ? "Generating…" : "Generate"}
            </button>
            {chosen && (
              <button onClick={() => generate(chosen.id)} disabled={busy}
                      className="modal-btn" data-testid="qa-image-regenerate">
                <RefreshCw className="h-3.5 w-3.5" /> Regenerate (replaces)
              </button>
            )}
          </div>
        </div>

        {/* Right: preview + insertion */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">Preview</div>
          {chosen ? (
            <>
              <div className="rounded-md overflow-hidden border border-[var(--pl-border)] bg-black/30 aspect-square flex items-center justify-center">
                <AuthedImage
                  testId="qa-image-preview"
                  path={chosen.url}
                  alt={chosen.prompt}
                  className="max-w-full max-h-full object-contain"
                />
              </div>
              <div className="mt-2 text-[10.5px] text-[var(--pl-text-muted)] leading-snug">
                <div><strong className="text-[var(--pl-text)]">{chosen.model}</strong> · {chosen.size} · {(chosen.bytes_len / 1024).toFixed(0)} KB</div>
                <div className="mt-1 line-clamp-2">{chosen.prompt}</div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <input value={insertPage} onChange={(e) => setInsertPage(e.target.value)}
                       placeholder="Page #" type="number" min="1"
                       className="w-20 h-8 rounded-md border border-[var(--pl-border)] bg-black/20 px-2 text-[12px]"
                       data-testid="qa-image-page-num" />
                <button onClick={() => insertIntoPage(chosen.id)} disabled={busy}
                        className="modal-btn-primary text-[11px]" data-testid="qa-image-insert">
                  <ArrowDownToLine className="h-3.5 w-3.5" /> Insert into page
                </button>
                <button onClick={() => doDelete(chosen.id)}
                        className="modal-btn text-[11px] text-rose-300" data-testid="qa-image-delete">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
              <div className="mt-2 text-[10px] text-[var(--pl-text-muted)] leading-snug">
                Positioning, cropping, and free-transform of images inside a page ship
                in Phase 2 (Object menu wiring). Inserted images render at the top of
                the selected page in Phase 1.1.
              </div>
            </>
          ) : (
            <div className="rounded-md border border-dashed border-[var(--pl-border)] p-6 text-center text-[11.5px] text-[var(--pl-text-muted)]">
              No image yet. Enter a prompt and click <strong>Generate</strong>.
            </div>
          )}

          {/* Assets library */}
          {assets.length > 0 && (
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">
                All images ({assets.length})
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {assets.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAssetId(a.id)}
                    data-testid={`qa-image-thumb-${a.id}`}
                    title={a.prompt}
                    className={"aspect-square rounded-md overflow-hidden border transition " +
                      (selectedAssetId === a.id
                        ? "border-[var(--pl-aqua)]/70 ring-1 ring-[var(--pl-aqua)]/40"
                        : "border-[var(--pl-border)]")}>
                    <AuthedImage path={a.url} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="modal-btn">Close</button>
      </ModalFooter>
    </ModalShell>
  );
}


/* ══════════════════════════════════════════════════════════════
 * KDP PREFLIGHT MODAL
 * ══════════════════════════════════════════════════════════════ */
export function KdpPreflightModal({ bookId, book, onClose }) {
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [report, setReport] = useState(null);
  const [settings, setSettings] = useState({
    trim_size: (book?.print_settings?.trim_size) || "6x9",
    bleed_inches: (book?.print_settings?.bleed_inches ?? 0.125),
    paper_type: (book?.print_settings?.paper_type) || "cream",
    inside_margin_inches: (book?.print_settings?.inside_margin_inches ?? 0.5),
    estimated_pages: (book?.print_settings?.estimated_pages) || "",
  });

  const run = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const body = { settings: {
        ...settings,
        bleed_inches: Number(settings.bleed_inches),
        inside_margin_inches: Number(settings.inside_margin_inches),
        estimated_pages: settings.estimated_pages ? Number(settings.estimated_pages) : undefined,
      }};
      const r = await api.post(`/api/book-lab/books/${bookId}/preflight`, body);
      setReport(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [bookId, settings]);
  useEffect(() => { run(); }, [run]);

  return (
    <ModalShell title="KDP preflight" icon={ShieldCheck} onClose={onClose}
                testId="qa-modal-kdp" wide>
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1 space-y-2 border-r border-[var(--pl-border)] pr-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">Print settings</div>
          <SettingRow label="Trim size">
            <select value={settings.trim_size}
              onChange={(e) => setSettings({ ...settings, trim_size: e.target.value })}
              className="modal-input" data-testid="qa-kdp-trim">
              {["5x8","5.25x8","5.5x8.5","6x9","6.14x9.21","7x10","8.5x11"].map(t => <option key={t}>{t}</option>)}
            </select>
          </SettingRow>
          <SettingRow label="Bleed (in)">
            <select value={String(settings.bleed_inches)}
              onChange={(e) => setSettings({ ...settings, bleed_inches: Number(e.target.value) })}
              className="modal-input" data-testid="qa-kdp-bleed">
              <option value="0">0 (no bleed)</option>
              <option value="0.125">0.125 (with bleed)</option>
            </select>
          </SettingRow>
          <SettingRow label="Paper">
            <select value={settings.paper_type}
              onChange={(e) => setSettings({ ...settings, paper_type: e.target.value })}
              className="modal-input" data-testid="qa-kdp-paper">
              <option>white</option><option>cream</option><option>color</option>
            </select>
          </SettingRow>
          <SettingRow label="Inside margin (in)">
            <input type="number" step="0.001" value={settings.inside_margin_inches}
              onChange={(e) => setSettings({ ...settings, inside_margin_inches: e.target.value })}
              className="modal-input" data-testid="qa-kdp-inside" />
          </SettingRow>
          <SettingRow label="Est. pages">
            <input type="number" min="24" max="828" value={settings.estimated_pages}
              onChange={(e) => setSettings({ ...settings, estimated_pages: e.target.value })}
              placeholder="(auto)" className="modal-input" data-testid="qa-kdp-pages" />
          </SettingRow>
          <button onClick={run} disabled={busy}
                  className="modal-btn-primary w-full mt-2" data-testid="qa-kdp-rerun">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {busy ? "Running…" : "Re-run checks"}
          </button>
        </div>

        <div className="col-span-2 max-h-[60vh] overflow-y-auto pr-1">
          {err && <ErrLine>{err}</ErrLine>}
          {report && (
            <>
              <div className="flex items-center gap-2 mb-3">
                <StatusBadge status={report.status} large />
                <div className="text-[11.5px] text-[var(--pl-text-muted)]">
                  <strong className="text-[var(--pl-text)]">{report.summary.passed}</strong> passed ·
                  &nbsp;<strong className="text-yellow-300">{report.summary.warned}</strong> warned ·
                  &nbsp;<strong className="text-rose-300">{report.summary.failed}</strong> failed
                </div>
                <div className="flex-1" />
                <div className="text-[10.5px] text-[var(--pl-text-muted)]">
                  {report.summary.chapter_count} ch · {report.summary.total_words?.toLocaleString?.() || 0} words · ~{report.summary.estimated_pages} pp
                </div>
              </div>
              <div className="space-y-1">
                {report.checks.map((c) => (
                  <div key={c.id} data-testid={`qa-kdp-check-${c.id}`}
                       className="flex items-start gap-2 rounded-md border border-[var(--pl-border)] p-2">
                    <StatusBadge status={c.status} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12.5px] text-[var(--pl-text)] font-medium">{c.name}</div>
                      <div className="text-[11px] text-[var(--pl-text-muted)] mt-0.5 break-words">{c.message}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <ModalFooter>
        <button onClick={onClose} className="modal-btn">Close</button>
      </ModalFooter>
    </ModalShell>
  );
}


/* ══════════════════════════════════════════════════════════════
 * PRINT PACKAGE MODAL
 * ══════════════════════════════════════════════════════════════ */
export function PrintPackageModal({ bookId, book, onClose }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState({
    trim_size: (book?.print_settings?.trim_size) || "6x9",
    bleed_inches: (book?.print_settings?.bleed_inches ?? 0.125),
    paper_type: (book?.print_settings?.paper_type) || "cream",
    inside_margin_inches: (book?.print_settings?.inside_margin_inches ?? 0.5),
    estimated_pages: (book?.print_settings?.estimated_pages) || "",
  });

  const download = async () => {
    setBusy(true); setErr(null); setStatus(null);
    try {
      const body = { settings: {
        ...settings,
        bleed_inches: Number(settings.bleed_inches),
        inside_margin_inches: Number(settings.inside_margin_inches),
        estimated_pages: settings.estimated_pages ? Number(settings.estimated_pages) : undefined,
      }};
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/book-lab/books/${bookId}/print-package`,
        { method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${api.token()}` },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        let msg = `Package build failed (${res.status})`;
        try { const j = await res.json(); msg = j.detail || msg; } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const preflight = {
        status: res.headers.get("x-preflight-status"),
        failed: Number(res.headers.get("x-preflight-failed") || 0),
        warned: Number(res.headers.get("x-preflight-warned") || 0),
      };
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const name = (book?.title || "book").replace(/[^A-Za-z0-9\- _]+/g, "").replace(/\s+/g, "_");
      a.download = `${name}_print_package.zip`;
      a.click();
      setStatus({ ok: true, preflight, size: blob.size });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Print package (ZIP)" icon={Package} onClose={onClose}
                testId="qa-modal-print" wide>
      <div className="text-[11.5px] text-[var(--pl-text-muted)] mb-3 leading-relaxed">
        Builds a ZIP with: <code>manuscript.pdf</code>, <code>cover-placeholder.pdf</code> (with
        real trim/bleed/spine dimensions), <code>metadata.json</code>, <code>preflight-report.json</code>,
        and <code>asset-manifest.json</code>. The cover is a <strong>placeholder</strong> — replace it
        before publishing.
      </div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <SettingRow label="Trim size">
          <select value={settings.trim_size}
            onChange={(e) => setSettings({ ...settings, trim_size: e.target.value })}
            className="modal-input" data-testid="qa-print-trim">
            {["5x8","5.25x8","5.5x8.5","6x9","6.14x9.21","7x10","8.5x11"].map(t => <option key={t}>{t}</option>)}
          </select>
        </SettingRow>
        <SettingRow label="Bleed (in)">
          <select value={String(settings.bleed_inches)}
            onChange={(e) => setSettings({ ...settings, bleed_inches: Number(e.target.value) })}
            className="modal-input" data-testid="qa-print-bleed">
            <option value="0">0 (no bleed)</option>
            <option value="0.125">0.125 (with bleed)</option>
          </select>
        </SettingRow>
        <SettingRow label="Paper">
          <select value={settings.paper_type}
            onChange={(e) => setSettings({ ...settings, paper_type: e.target.value })}
            className="modal-input" data-testid="qa-print-paper">
            <option>white</option><option>cream</option><option>color</option>
          </select>
        </SettingRow>
        <SettingRow label="Inside margin (in)">
          <input type="number" step="0.001" value={settings.inside_margin_inches}
            onChange={(e) => setSettings({ ...settings, inside_margin_inches: e.target.value })}
            className="modal-input" data-testid="qa-print-inside" />
        </SettingRow>
        <SettingRow label="Est. pages">
          <input type="number" min="24" max="828" value={settings.estimated_pages}
            onChange={(e) => setSettings({ ...settings, estimated_pages: e.target.value })}
            placeholder="(auto)" className="modal-input" data-testid="qa-print-pages" />
        </SettingRow>
      </div>

      {err && <ErrLine>{err}</ErrLine>}
      {status?.ok && (
        <OkLine>
          Downloaded ZIP ({(status.size / 1024).toFixed(1)} KB).
          Preflight: {" "}
          <StatusBadge status={status.preflight.status} inline />
          {" "}({status.preflight.failed} failed · {status.preflight.warned} warned).
          <br />
          ⚠ Contains a PLACEHOLDER cover — replace it before publishing.
        </OkLine>
      )}

      <ModalFooter>
        <button onClick={onClose} className="modal-btn">Close</button>
        <button onClick={download} disabled={busy}
                className="modal-btn-primary" data-testid="qa-print-download">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowDownToLine className="h-3.5 w-3.5" />}
          {busy ? "Building…" : "Build & download"}
        </button>
      </ModalFooter>
    </ModalShell>
  );
}


/* ══════════════════════════════════════════════════════════════
 * Shared UI primitives
 * ══════════════════════════════════════════════════════════════ */
function ModalShell({ title, icon: Icon, onClose, children, testId, wide }) {
  return (
    <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
         onClick={onClose} data-testid={testId}>
      <div onClick={(e) => e.stopPropagation()}
           className={"bg-[var(--pl-bg)] border border-[var(--pl-border)] rounded-xl shadow-2xl w-full flex flex-col " +
                      (wide ? "max-w-3xl" : "max-w-md")}
           style={{ color: "var(--pl-text)" }}>
        <style>{`
          .modal-btn { display:inline-flex; align-items:center; gap:6px; height:32px; padding:0 10px;
            border-radius:6px; border:1px solid var(--pl-border); background:transparent;
            color:var(--pl-text); font-size:11.5px; cursor:pointer; }
          .modal-btn:hover:not(:disabled) { border-color: color-mix(in srgb, var(--pl-aqua) 40%, transparent); }
          .modal-btn:disabled { opacity:.5; cursor:not-allowed; }
          .modal-btn-primary { display:inline-flex; align-items:center; gap:6px; height:32px; padding:0 12px;
            border-radius:6px; border:0; background:linear-gradient(135deg,#00B8A9,#27E6D1);
            color:#061414; font-weight:700; font-size:11.5px; cursor:pointer; }
          .modal-btn-primary:disabled { opacity:.5; cursor:not-allowed; }
          .modal-input { width:100%; height:30px; padding:0 8px; border-radius:6px;
            border:1px solid var(--pl-border); background:rgba(0,0,0,0.2);
            color:var(--pl-text); font-size:12px; }
        `}</style>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[var(--pl-border)]">
          <Icon className="h-4 w-4 text-[var(--pl-aqua)]" />
          <div className="text-[13px] font-bold">{title}</div>
          <div className="flex-1" />
          <button onClick={onClose} className="modal-btn" aria-label="Close" data-testid="qa-modal-close">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ maxHeight: "80vh" }}>{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ children }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[var(--pl-border)]">
      {children}
    </div>
  );
}

function SettingRow({ label, children }) {
  return (
    <label className="block">
      <div className="text-[9.5px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">{label}</div>
      {children}
    </label>
  );
}

function ErrLine({ children }) {
  return (
    <div className="mt-2 p-2 rounded-md text-[11.5px] leading-snug"
         style={{ background: "rgba(220,38,38,0.12)", color: "#FCA5A5" }} data-testid="qa-err">
      {children}
    </div>
  );
}
function OkLine({ children }) {
  return (
    <div className="mt-2 p-2 rounded-md text-[11.5px] leading-snug"
         style={{ background: "rgba(34,197,94,0.12)", color: "#86EFAC" }} data-testid="qa-ok">
      {children}
    </div>
  );
}

function StatusBadge({ status, large, inline }) {
  const map = {
    pass: { fg: "#86EFAC", bg: "rgba(34,197,94,0.15)", Icon: CheckCircle2, label: "PASS" },
    warn: { fg: "#FCD34D", bg: "rgba(234,179,8,0.15)", Icon: AlertTriangle, label: "WARN" },
    fail: { fg: "#FCA5A5", bg: "rgba(220,38,38,0.15)", Icon: XCircle, label: "FAIL" },
  };
  const p = map[status] || map.warn;
  const size = large ? 13 : 11;
  return (
    <span className={"inline-flex items-center gap-1 rounded-md " + (inline ? "" : "shrink-0")}
          style={{ background: p.bg, color: p.fg, padding: large ? "5px 9px" : "2px 6px",
                   fontSize: size, fontWeight: 800, letterSpacing: 0.4 }}>
      <p.Icon className={large ? "h-4 w-4" : "h-3 w-3"} />
      {p.label}
    </span>
  );
}
